/**
 * Moderation types — abuse reports.
 *
 * Due provenienze, un solo posto: la chat di una partita (CHAT_REPORT_*) e il
 * profilo pubblico di un utente (PROFILE_REPORT_*, PLAYER_REPORT_*). Cambia
 * soltanto la PROVA — nella chat il trascritto della room, dal profilo niente,
 * perche' il profilo e' pubblico e non va fotografato — e quindi cambiano i
 * motivi; tutto il resto (le due parti, la nota, lo stato, la retention) e' lo
 * stesso, e sta nella stessa tabella `chat_reports`.
 *
 * Ogni elenco di motivi e' BOTH la fonte del radiogruppo del client AND l'enum
 * zod del server AND lo specchio del CHECK in migration 070/120, quindi vive
 * qui: copie separate finirebbero per divergere.
 */

/**
 * Why a player is being reported.
 *
 * No 'cheating' entry on purpose: engine assistance is a different flow with
 * different evidence (the moves, not the chat), and offering it here would
 * attract reports this table cannot support.
 */
export const CHAT_REPORT_REASONS = [
  'insult',
  'harassment',
  'hate_speech',
  'spam',
  'other',
] as const;
export type ChatReportReason = (typeof CHAT_REPORT_REASONS)[number];

/**
 * Vale per TUTTE le segnalazioni, non solo per quelle di chat: il nome e' quello
 * di quando c'era solo la chat, ma la colonna, il CHECK e la sweep sono gli
 * stessi per entrambe le provenienze. Un gemello `PROFILE_REPORT_CONFIG` con gli
 * stessi due numeri sarebbe solo un secondo posto da cui divergere.
 */
export const CHAT_REPORT_CONFIG = {
  /** Must match the chat_reports_note_max_len CHECK (migration 070). */
  MAX_NOTE_LENGTH: 500,
  /** Must match the retention sweep in auth/cleanup.service.ts. */
  RETENTION_DAYS: 90,
} as const;

export interface CreateChatReportPayload {
  roomId: string;
  reason: ChatReportReason;
  /**
   * `ChatMessage.id` of the offending message, when the client can point at
   * one. Ignored (stored as null) if it is no longer in the room transcript.
   */
  messageId?: string;
  note?: string;
}

export interface CreateChatReportResponse {
  success: true;
  reportId: string;
}

/**
 * Server error codes the client maps to distinct i18n messages. They travel as
 * the `error` field of the JSON body and reach the client as `ApiRequestError.code`.
 */
export const CHAT_REPORT_ERRORS = {
  /** The room (and with it the authoritative transcript) is gone — 410. */
  TRANSCRIPT_GONE: 'CHAT_TRANSCRIPT_GONE',
  /** This reporter already filed a report for this room — 409. */
  ALREADY_FILED: 'CHAT_REPORT_ALREADY_FILED',
  /** Opponent is a bot, or has no account to report — 409. */
  NOT_REPORTABLE: 'CHAT_REPORT_NOT_REPORTABLE',
} as const;

/**
 * Perche' si segnala un utente dal suo PROFILO.
 *
 * Insieme DIVERSO da CHAT_REPORT_REASONS, non un suo sovrainsieme: dal profilo
 * si vedono il nome e l'avatar, non cio' che e' stato detto in una partita.
 * 'insult' e 'hate_speech' chiederebbero un trascritto che qui non esiste, e
 * 'offensive_avatar' non vuol dire niente dentro una chat — per questo il CHECK
 * della migration 120 lega il motivo alla provenienza invece di accettare
 * l'unione dei due elenchi.
 *
 * Niente 'cheating', per la stessa ragione scritta sopra per la chat: e' un
 * flusso con prove diverse (le mosse), e offrirlo qui attirerebbe segnalazioni
 * che questa tabella non sa sostenere.
 */
export const PROFILE_REPORT_REASONS = [
  'offensive_username',
  'offensive_avatar',
  'harassment',
  'spam',
  'other',
] as const;
export type ProfileReportReason = (typeof PROFILE_REPORT_REASONS)[number];

export interface CreatePlayerReportPayload {
  /**
   * Chi si segnala. Il client manda il NOME, non un id: il profilo pubblico non
   * porta lo userId (vedi PublicProfile), e il server lo risolve da se' — che e'
   * anche la ragione per cui nessuno puo' scegliersi una vittima diversa da
   * quella il cui profilo sta guardando.
   */
  username: string;
  reason: ProfileReportReason;
  note?: string;
}

export interface CreatePlayerReportResponse {
  success: true;
  reportId: string;
}

/**
 * Codici che il client mappa su messaggi distinti. Viaggiano nel campo `error`
 * del corpo JSON e arrivano al client come `ApiRequestError.code`.
 */
export const PLAYER_REPORT_ERRORS = {
  /** Nessun account con quel nome, oppure e' un ospite o un bot — 404. */
  USER_NOT_FOUND: 'PLAYER_REPORT_USER_NOT_FOUND',
  /** Questo segnalante ha gia' una segnalazione APERTA su questo utente — 409. */
  ALREADY_FILED: 'PLAYER_REPORT_ALREADY_FILED',
  /** Non si segnala se stessi — 409. */
  SELF: 'PLAYER_REPORT_SELF',
} as const;
